// src/api/transactionsApi.js
import { api } from "./client";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const BASE = `${API_BASE_URL}api/transactions/`;

/** Build common headers (lang, token, optional store id) */
const buildHeaders = (extra = {}) => {
  const lang = localStorage.getItem("lang") || "en";
  const token = localStorage.getItem("token");
  const storeId = localStorage.getItem("storeId"); // optional, used by backend as X-Store-Id

  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    "Accept-Language": lang,
    ...(storeId ? { "X-Store-Id": storeId } : {}),
    ...extra,
  };
};

/** Remove undefined/null from params so we don’t send junk query keys */
const cleanParams = (obj = {}) =>
  Object.fromEntries(
    Object.entries(obj).filter(
      ([, v]) => v !== undefined && v !== null && v !== ""
    )
  );

/**
 * Create a new transaction (POS sale).
 * body example:
 * {
 *   orderId, type, receivedAmount, payingAmount, change,
 *   paymentReceiver, paymentNote, saleNote, staffNote,
 *   customerId, totalAmount,
 *   items: [{ productId, name, quantity, price, variantId?, warehouse? }]
 * }
 * POST /api/transactions
 */
export const createTransaction = async (transactionData, opts = {}) => {
  try {
    const headers = buildHeaders(
      opts.storeId ? { "X-Store-Id": opts.storeId } : {}
    );
    const { data } = await api.post(`${BASE}`, transactionData, { headers });
    return data;
  } catch (error) {
    console.error("Create Transaction API Error:", error);
    throw error.response?.data || { message: "Failed to create transaction" };
  }
};

/**
 * Get all transactions (optionally filtered with query params)
 * GET /api/transactions
 * params: { page, limit, dateFrom, dateTo, type, customerId, search }
 * NOTE: Your current backend returns { success, transactions }
 */
export const getAllTransactions = async (params = {}, opts = {}) => {
  try {
    const headers = buildHeaders(
      opts.storeId ? { "X-Store-Id": opts.storeId } : {}
    );
    const q = cleanParams(params);
    const { data } = await api.get(`${BASE}`, { params: q, headers });
    return data;
  } catch (error) {
    console.error("Get Transactions API Error:", error);
    throw error.response?.data || { message: "Failed to fetch transactions" };
  }
};

/**
 * Convenience helper for date range queries
 */
export const getTransactionsByDate = async (
  dateFrom,
  dateTo,
  extra = {},
  opts = {}
) => {
  return getAllTransactions({ ...extra, dateFrom, dateTo }, opts);
};

/**
 * POS Items Sold Report
 * GET /api/transactions/reports/pos-items
 * Query:
 *  - from=YYYY-MM-DD
 *  - to=YYYY-MM-DD
 *  - groupBy=product|variant|warehouse|day   (default: product)
 *  - types=cash,card                         (optional)
 *  - page, limit                             (optional, if you paginate)
 *
 * Returns whatever your controller responds with (e.g. { success, data, ... }).
 */
export const getPosItemsSoldReport = async (
  { from, to, groupBy = "product", types, page, limit } = {},
  opts = {}
) => {
  try {
    const headers = buildHeaders(
      opts.storeId ? { "X-Store-Id": opts.storeId } : {}
    );
    const params = cleanParams({ from, to, groupBy, types, page, limit });
    const { data } = await api.get(`${BASE}/reports/pos-items`, {
      params,
      headers,
    });
    return data;
  } catch (error) {
    console.error("POS Items Sold Report API Error:", error);
    throw error.response?.data || { message: "Failed to fetch items report" };
  }
};
